import {inject} from '@angular/core';
import {CanActivateFn, Router, Routes} from '@angular/router';
import {Dashboard} from './component/dashboard/dashboard';
import {Role} from './enum/role';
import {roleGuard} from './guard/role.guard';
import {CurrentUserService} from './service/current-user';
import {SelectSectorService} from './service/sector-selection';
import {LandingGateSupporter} from "./supporter/landing-gate-supporter";

function resolveLandingRoute(roles: Role[], sectorId?: string, companyId?: string): string {
  if (roles.includes(Role.SUPERADMIN)) return '/sectoren';

  const isOnlyAdmin = roles.length > 0 && roles.every(role => role === Role.ADMIN);

  return isOnlyAdmin && sectorId && companyId
    ? `/sectoren/${sectorId}/bedrijven/${companyId}/gebruikers`
    : '/dashboard';
}

const firstRouteRedirect: CanActivateFn = async () => {
  const router = inject(Router);
  const currentUser = inject(CurrentUserService);
  const sectorSelection = inject(SelectSectorService);
  const landingGate = inject(LandingGateSupporter);

  await landingGate.ready;

  const target = resolveLandingRoute(
    currentUser.roles(),
    sectorSelection.getSelectedSectorId(),
    sectorSelection.getSelectedCompanyId(),
  );

  return router.parseUrl(target);
};

export const routes: Routes = [
  {
    path: '',
    canActivate: [firstRouteRedirect],
    component: Dashboard,
  },
  {
    path: 'dashboard',
    component: Dashboard,
  },
  {
    path: 'sectoren',
    children: [
      {
        path: '',
        loadComponent: () =>
          import('./component/sector/sector-overview/sector-overview').then(
            (m) => m.SectorOverview
          ),
        canActivate: [roleGuard],
        data: {roles: [Role.SUPERADMIN]},
      },
      {
        path: 'aanmaken',
        loadComponent: () =>
          import('./component/sector/sector-management/sector-management').then(
            (m) => m.SectorManagement
          ),
        canActivate: [roleGuard],
        data: {roles: [Role.SUPERADMIN]},
      },
      {
        path: 'bedrijven',
        loadComponent: () =>
          import(
            './component/sector/sector-company-overview/sector-company-overview'
            ).then((m) => m.SectorCompanyOverview),
        canActivate: [roleGuard],
        data: {roles: [Role.SUPERADMIN]},
      },
      {
        path: 'assessments',
        children: [
          {
            path: '',
            loadComponent: () =>
              import('./component/assessment/assessment-overview/assessment-overview').then(
                (m) => m.AssessmentOverview,
              ),
            canActivate: [roleGuard],
            data: {
              roles: [
                Role.SUPERADMIN,
              ],
            },
          },
          {
            path: 'builder',
            loadComponent: () =>
              import('./component/assessment/assessment-builder/assessment-builder').then(
                (m) => m.AssessmentBuilder,
              ),
            canActivate: [roleGuard],
            data: {roles: [Role.SUPERADMIN]},
          },
        ],
      },
      {
        path: ':id',
        children: [
          {
            path: '',
            loadComponent: () =>
              import(
                './component/sector/sector-management/sector-management'
                ).then((m) => m.SectorManagement),
            canActivate: [roleGuard],
            data: {roles: [Role.SUPERADMIN]},
          },
          {
            path: 'bedrijven',
            children: [
              {
                path: 'aanmaken',
                loadComponent: () =>
                  import(
                    './component/company/company-management/company-management'
                    ).then((m) => m.CompanyManagement),
                canActivate: [roleGuard],
                data: {roles: [Role.SUPERADMIN]},
              },
              {
                path: 'kopieren',
                loadComponent: () => import('./component/career/company-career-house-duplicator/company-career-house-duplicator').then(m => m.CompanyCareerHouseDuplicator),
                canActivate: [roleGuard],
                data: {roles: [Role.SUPERADMIN]},
              },
              {
                path: ':companyId',
                children: [
                  {
                    path: '',
                    loadComponent: () =>
                      import(
                        './component/company/company-management/company-management'
                        ).then((m) => m.CompanyManagement),
                    canActivate: [roleGuard],
                    data: {roles: [Role.SUPERADMIN]},
                  },
                  {
                    path: 'gebruikers',
                    children: [
                      {
                        path: '',
                        loadComponent: () => import("./component/company/company-management/company-management").then(m => m.CompanyManagement),
                        canActivate: [roleGuard],
                        data: {roles: [Role.ADMIN]},
                      },
                    ],
                  },
                  {
                    path: 'functies',
                    children: [
                      {
                        path: '',
                        loadComponent: () => import("./component/company/career-function-overview/career-function-overview").then(m => m.CareerFunctionOverview)
                      },
                      {
                        path: 'toevoegen',
                        loadComponent: () => import("./component/company/career-function-management/career-function-management").then(m => m.CareerFunctionManagement)
                      },
                      {
                        path: ':functionId',
                        loadComponent: () => import("./component/company/career-function-management/career-function-management").then(m => m.CareerFunctionManagement)
                      }
                    ],
                    canActivate: [roleGuard],
                    data: {roles: [Role.ADMIN]}
                  },
                  {
                    path: 'organogram',
                    loadComponent: () =>
                      import('./component/organogram/organogram-overview/organogram-overview').then(
                        (m) => m.OrganogramOverview
                      ),
                    canActivate: [roleGuard],
                    data: {roles: [Role.TRAINEE, Role.EMPLOYEE, Role.MANAGER, Role.ADMIN, Role.SUPERADMIN]},
                  },
                  {
                    path: 'matching',
                    children: [
                      {
                        path: '',
                        loadComponent: () => import("./component/matching/matching-overview/matching-overview").then(m => m.MatchingOverview),
                      },
                      {
                        path: ':matchingId/resultaten',
                        loadComponent: () => import("./component/matching/matching-score-overview/matching-score-overview").then(m => m.MatchingScoreOverview),
                      }
                    ],
                    canActivate: [roleGuard],
                    data: {roles: [Role.MANAGER]}
                  },
                ],
              },
              {
                path: '',
                loadComponent: () =>
                  import(
                    './component/sector/sector-company-overview/sector-company-overview'
                    ).then((m) => m.SectorCompanyOverview),
                canActivate: [roleGuard],
                data: {roles: [Role.SUPERADMIN]},
              },
            ],
          },
          {
            path: 'taken', children: [
              {
                path: '',
                loadComponent: () => import('./component/task/task-overview/task-overview').then(m => m.TaskOverview),
                canActivate: [roleGuard],
                data: {roles: [Role.SUPERADMIN]},
              },
              {
                path: 'import',
                loadComponent: () => import("./component/skill/skill-import/skill-import").then(m => m.SkillImport),
                canActivate: [roleGuard],
                data: {roles: [Role.SUPERADMIN]},
              },
            ]
          },
          {
            path: 'competenties', children: [
              {
                path: '',
                loadComponent: () => import("./component/competence/competence-overview/competence-overview").then(m => m.CompetenceOverview),
                canActivate: [roleGuard],
                data: {roles: [Role.SUPERADMIN]},
              },
              {
                path: 'import',
                loadComponent: () => import("./component/competence/competence-import/competence-import").then(m => m.CompetenceImport),
                canActivate: [roleGuard],
                data: {roles: [Role.SUPERADMIN]},
              },
            ]
          },
          {
            path: 'kennis', children: [
              {
                path: '',
                loadComponent: () => import("./component/knowledge/knowledge-overview/knowledge-overview").then(m => m.KnowledgeOverview),
                canActivate: [roleGuard],
                data: {roles: [Role.SUPERADMIN]},
              },
              {
                path: 'import',
                loadComponent: () => import("./component/knowledge/knowledge-import/knowledge-import").then(m => m.KnowledgeImport),
                canActivate: [roleGuard],
                data: {roles: [Role.SUPERADMIN]},
              },
            ]
          },
          {
            path: 'carrierepaden',
            loadComponent: () => import("./component/career/career-overview/career-overview").then(m => m.CareerOverview),
            canActivate: [roleGuard],
            data: {roles: [Role.SUPERADMIN, Role.ADMIN, Role.MANAGER, Role.COACH, Role.POPPMMANAGER, Role.EMPLOYEE, Role.TRAINEE]},
          },
          {
            path: 'ontwikkeling', children: [
              {
                path: '',
                loadComponent: () => import("./component/user/user-performance-selector/user-performance-selector").then(m => m.UserPerformanceSelector),
                canActivate: [roleGuard],
                data: {roles: [Role.MANAGER, Role.COACH]},
              },
              {
                path: ':userId',
                loadComponent: () => import("./component/user/user-performance-management/user-performance-management").then(m => m.UserPerformanceManagement),
                canActivate: [roleGuard],
                data: {roles: [Role.ADMIN, Role.SUPERADMIN, Role.MANAGER, Role.COACH, Role.EMPLOYEE, Role.TRAINEE]},
              }
            ]
          },
          {
            path: 'feedback',
            children: [
              {
                path: 'pop/:popId',
                loadComponent: () =>
                  import('./component/feedback/feedback-popup/feedback-popup').then(
                    (m) => m.FeedbackPopup,
                  ),
              },
              {
                path: 'pm/:pmId',
                loadComponent: () =>
                  import('./component/feedback/feedback-popup/feedback-popup').then(
                    (m) => m.FeedbackPopup,
                  ),
              },
            ],
            canActivate: [roleGuard],
            data: {roles: [Role.ADMIN, Role.SUPERADMIN, Role.MANAGER, Role.COACH, Role.EMPLOYEE, Role.TRAINEE]},
          },
          {
            path: 'assessments',
            children: [
              {
                path: '',
                loadComponent: () =>
                  import('./component/assessment/assessment-overview/assessment-overview').then(
                    (m) => m.AssessmentOverview,
                  ),
                canActivate: [roleGuard],
                data: {
                  roles: [
                    Role.SUPERADMIN,
                    Role.ADMIN,
                    Role.MANAGER,
                    Role.COACH,
                    Role.POPPMMANAGER,
                    Role.EMPLOYEE,
                    Role.TRAINEE,
                  ],
                },
              },
              {
                path: 'builder',
                loadComponent: () =>
                  import('./component/assessment/assessment-builder/assessment-builder').then(
                    (m) => m.AssessmentBuilder,
                  ),
                canActivate: [roleGuard],
                data: {roles: [Role.SUPERADMIN]},
              },
              {
                path: ':assessmentId/fill',
                loadComponent: () =>
                  import('./component/assessment/assessment-fill/assessment-fill').then(
                    (m) => m.AssessmentFill,
                  ),
                canActivate: [roleGuard],
                data: {
                  roles: [
                    Role.EMPLOYEE,
                    Role.TRAINEE,
                  ],
                },
              },
              {
                path: ':assessmentId',
                loadComponent: () =>
                  import('./component/assessment/assessment-management/assessment-management').then(
                    (m) => m.AssessmentManagement,
                  ),
                canActivate: [roleGuard],
                data: {
                  roles: [
                    Role.SUPERADMIN,
                    Role.ADMIN,
                    Role.MANAGER,
                    Role.COACH,
                    Role.POPPMMANAGER,
                    Role.EMPLOYEE,
                    Role.TRAINEE,
                  ],
                },
              },
            ],
          },
          {
            path: 'afspraken',
            loadComponent: () => import("./component/planner/planner-overview/planner-overview").then(m => m.PlannerOverview),
            canActivate: [roleGuard],
            data: {roles: [Role.MANAGER, Role.COACH, Role.POPPMMANAGER, Role.EMPLOYEE, Role.TRAINEE]},
          },
        ],
      },
    ],
  },
  {
    path: '**',
    redirectTo: 'dashboard',
  },
];
